import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { getRiskById, deleteRisk } from '../../api/riskApi';
import { getRiskLevelLabel, getRiskLevelClass, STRENGTH_OPTIONS, FREQUENCY_OPTIONS } from '../../utils/riskUtils';
import './risk.css';

const valColor = (v) => { const n = Number(v); if (n >= 3) return 'danger'; if (n === 2) return 'warning'; return 'success'; };

const largeBadgeStyle = { fontSize: '1rem', padding: '6px 14px' };

function FreqBadge({ value, large }) {
  if (!value) return <span className="text-muted">-</span>;
  const label = FREQUENCY_OPTIONS.find(o => String(o.value) === String(value))?.label?.split(' ')[0] || '-';
  return <span className={`badge bg-${valColor(value)}`} style={large ? largeBadgeStyle : undefined}>{value} - {label}</span>;
}

function StrBadge({ value, large }) {
  if (!value) return <span className="text-muted">-</span>;
  const label = STRENGTH_OPTIONS.find(o => String(o.value) === String(value))?.label?.split(' ')[0] || '-';
  return <span className={`badge bg-${valColor(value)}`} style={large ? largeBadgeStyle : undefined}>{value} - {label}</span>;
}

function LevelBadge({ value, large }) {
  if (!value) return <span className="text-muted">-</span>;
  return (
    <span
      className={`badge bg-${getRiskLevelClass(value)}`}
      style={large ? { fontSize: '1rem', padding: '6px 14px' } : undefined}
    >
      {value} - {getRiskLevelLabel(value)}
    </span>
  );
}

function parseChargeList(chargeInfo) {
  if (!chargeInfo) return [];
  try {
    const parsed = typeof chargeInfo === 'string' ? JSON.parse(chargeInfo) : chargeInfo;
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    return [];
  }
}

function RiskView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);
  const [risk, setRisk] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getRiskById(id, token)
      .then(data => {
        setRisk(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('데이터 조회 실패:', err);
        alert('데이터를 불러오지 못했습니다.');
        navigate('/risk');
      });
  }, [id, token, navigate]);

  const handleDelete = async () => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    try {
      await deleteRisk(id, token);
      navigate('/risk');
    } catch (err) {
      console.error('삭제 실패:', err);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  if (loading) {
    return (
      <div className="text-center text-muted mt-5">
        <div className="spinner-border text-secondary mb-3" role="status" />
        <p>불러오는 중...</p>
      </div>
    );
  }

  const chargeList = parseChargeList(risk.charge_info);
  const isOwnerOrAdmin = user && (user.role === 'admin' || user.userid === risk.write_id);

  return (
    <div className="container-fluid view-post">
      <div className="row">
        <div className="col-12">

          {/* 헤더 */}
          <div className="risk-header">
            <h4>위험성 평가 상세</h4>
            <div className="risk-header-actions">
              <button className="btn btn-outline-primary btn-sm" onClick={() => navigate('/risk')}>
                목록
              </button>
              {isOwnerOrAdmin && (
                <>
                  <button className="btn btn-outline-info btn-sm" onClick={() => navigate(`/risk/edit/${id}`)}>
                    수정
                  </button>
                  <button className="btn btn-outline-danger btn-sm" onClick={handleDelete}>
                    삭제
                  </button>
                </>
              )}
            </div>
          </div>

          {/* 작성자 / 작성일 */}
          <div className="risk-meta text-muted">
            <small>작성자: {risk.write_name}</small>
            <small>{new Date(risk.created_at).toLocaleString()}</small>
          </div>

          {/* 분류 정보 */}
          <div className="card mb-3">
            <div className="card-header fw-bold">분류 정보</div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-3">
                  <label className="text-muted small">분류 넘버</label>
                  <p className="mb-0">{risk.risk_category_num || '-'}</p>
                </div>
                <div className="col-md-9">
                  <label className="text-muted small">분류 내용</label>
                  <p className="mb-0">{risk.risk_category_context || '-'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* 위험 요인 및 대책 */}
          <div className="card mb-3">
            <div className="card-header fw-bold">위험 요인 및 대책</div>
            <div className="card-body">
              <div className="mb-3">
                <label className="text-muted small">유해위험 요인</label>
                <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{risk.risk_factors || '-'}</p>
              </div>
              <hr />
              <div>
                <label className="text-muted small">감소 대책</label>
                <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{risk.risk_reduction_measures || '-'}</p>
              </div>
            </div>
          </div>

          {/* 위험성 평가 */}
          <div className="card mb-3">
            <div className="card-header fw-bold">위험성 평가</div>
            <div className="card-body">
              <div className="row g-4">
                <div className="col-md-6">
                  <div className="fw-bold text-muted small mb-2">현재 위험성</div>
                  <div className="d-flex align-items-center justify-content-between py-2 border-bottom">
                    <span className="text-muted small">빈도</span>
                    <FreqBadge value={risk.current_risk_frequency} large />
                  </div>
                  <div className="d-flex align-items-center justify-content-between py-2 border-bottom">
                    <span className="text-muted small">강도</span>
                    <StrBadge value={risk.current_risk_strength} large />
                  </div>
                  <div className="d-flex align-items-center justify-content-between py-2">
                    <span className="text-muted small">위험성</span>
                    <LevelBadge value={risk.current_risk_level} large />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="fw-bold text-muted small mb-2">개선 후 위험성</div>
                  <div className="d-flex align-items-center justify-content-between py-2 border-bottom">
                    <span className="text-muted small">빈도</span>
                    <FreqBadge value={risk.after_risk_frequency} large />
                  </div>
                  <div className="d-flex align-items-center justify-content-between py-2 border-bottom">
                    <span className="text-muted small">강도</span>
                    <StrBadge value={risk.after_risk_strength} large />
                  </div>
                  <div className="d-flex align-items-center justify-content-between py-2">
                    <span className="text-muted small">위험성</span>
                    <LevelBadge value={risk.after_risk_level} large />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 담당자 */}
          <div className="card mb-3">
            <div className="card-header fw-bold">담당자 정보</div>
            <div className="card-body">
              {chargeList.length > 0 ? (
                <p className="mb-0">
                  {chargeList.map((c, idx) => {
                    const label = `${c.name || '-'}${c.position ? ' / ' + c.position : ''}${c.department ? ' (' + c.department + ')' : ''}`;
                    return (
                      <span key={c.emp_no || idx}>
                        {idx > 0 && <span className="text-muted">, </span>}
                        {label}
                      </span>
                    );
                  })}
                </p>
              ) : (
                <span className="text-muted">-</span>
              )}
            </div>
          </div>

          {/* 일정 */}
          <div className="card mb-3">
            <div className="card-header fw-bold">일정</div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="text-muted small">조치요구일</label>
                  <p className="mb-0">{risk.request_date || '-'}</p>
                </div>
                <div className="col-md-4">
                  <label className="text-muted small">완료일</label>
                  <p className="mb-0">{risk.complete_date || '-'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* 개선 이미지 */}
          {(risk.before_img_path || risk.after_img_path) && (
            <div className="card mb-4">
              <div className="card-header fw-bold">개선 이미지</div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="text-muted small">개선 전</label>
                    {risk.before_img_path ? (
                      <img src={risk.before_img_path} alt="개선 전"
                        className="img-fluid d-block mt-1 rounded border" style={{ maxHeight: 300 }} />
                    ) : <p className="mb-0 text-muted">-</p>}
                  </div>
                  <div className="col-md-6">
                    <label className="text-muted small">개선 후</label>
                    {risk.after_img_path ? (
                      <img src={risk.after_img_path} alt="개선 후"
                        className="img-fluid d-block mt-1 rounded border" style={{ maxHeight: 300 }} />
                    ) : <p className="mb-0 text-muted">-</p>}
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export default RiskView;
